import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { investmentsInfoService } from '../../../../services/investmentsInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('InvestmentsInfoData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const InvestmentsInfoDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  const [investmentsData, setInvestmentsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadInvestmentsData();
  }, [targetUserId]);

  const loadInvestmentsData = async () => {
    try {
      setLoading(true);
      const data = await investmentsInfoService.getAllInvestmentsInfo(targetUserId);
      
      if (data && data.length > 0) {
        // Load portal credentials for each investment (only for the owner, not viewOnly mode)
        const updatedData = await Promise.all(
          data.map(async (investment) => {
            // Skip loading portal credentials in viewOnly mode - they are protected until EOL
            if (!viewOnly || isDeathVerifiedForSelectedUser) {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for investment ID:', investment.id);
                const portalData = await vaultService.getPortalCredentials(investment.id, 'investments');
                logger.debug('[Portal Debug] Portal data retrieved for investment:', investment.id, portalData);
                if (portalData) {
                  investment.username = portalData.username;
                  investment.password = portalData.password;
                  investment.accountNumber = portalData.accountNumber;
                  logger.debug('[Portal Debug] Assigned credentials to investment data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for investment:', investment.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for investment:', investment.id, vaultError);
              }
            }
            return investment;
          })
        );

        logger.debug('[Data Debug] Final investments data being set:', updatedData.length, 'investments');
        setInvestmentsData(updatedData);
      }
    } catch (error) {
      logger.error('Error loading investments data:', error);
      Alert.alert('Error', 'Failed to load investments information');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number | string) => {
    if (!amount) return '';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleEditInvestment = (investment: any) => {
    navigation.navigate('InvestmentsInfoInput', { 
      investmentId: investment.id, 
      isEdit: true 
    });
  };

  const handleDeleteInvestment = async (investment: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this investment account?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await investmentsInfoService.deleteInvestmentsInfo(investment.id);
            
            // Reload data after deletion
            await loadInvestmentsData();
            
            Alert.alert('Deleted', 'Investment information deleted.');
          } catch (e) {
            logger.error('Delete investment failed:', e);
            Alert.alert('Error', 'Failed to delete investment');
          }
        } 
      },
    ]);
  };

  const handleAddInvestment = () => {
    navigation.navigate('InvestmentsInfoInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Investments Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading investments information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!investmentsData || investmentsData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Investments Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly ? 'This user has not added investment information yet' : 'No investment information found'}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddInvestment}
              >
                <Text style={styles.addButtonText}>Add Investment Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Investments Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {investmentsData.map((investment, index) => (
            <View key={investment.id || index} style={styles.investmentCard}>
              <View style={styles.investmentHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.institutionName}>
                    {investment.financialInstitution || 'Unnamed Institution'}
                  </Text>
                  <Text style={styles.investmentType}>
                    {investment.investmentType || 'No type specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editInvestmentButton}
                      onPress={() => handleEditInvestment(investment)}
                    >
                      <Text style={styles.editInvestmentButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteInvestmentButton}
                      onPress={() => handleDeleteInvestment(investment)}
                    >
                      <Text style={styles.deleteInvestmentButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Financial Institution" value={investment.financialInstitution || ''} />
              <InfoItem label="Account Type" value={investment.accountType || ''} />
              <InfoItem label="Investment Type" value={investment.investmentType || ''} />
              <InfoItem label="Account Value" value={investment.accountValue ? formatCurrency(investment.accountValue) : ''} />
              <InfoItem label="Online Account URL" value={investment.onlineAccountUrl || ''} />
              <InfoItem label="Customer Service Phone" value={formatPhoneNumber(investment.customerServicePhone) || ''} />
              <InfoItem label="Support Email" value={investment.supportEmail || ''} />
              <InfoItem label="Beneficiary Info" value={investment.beneficiaryInfo || ''} />
              
              {investment.accountNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Account Number</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showAccountNumber[investment.id] ? investment.accountNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowAccountNumber(prev => ({ ...prev, [investment.id]: !prev[investment.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showAccountNumber[investment.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {investment.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[investment.id] ? investment.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [investment.id]: !prev[investment.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[investment.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {investment.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[investment.id] ? investment.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [investment.id]: !prev[investment.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[investment.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={investment.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherInvestmentButton}
                onPress={handleAddInvestment}
              >
                <Text style={styles.addAnotherInvestmentButtonText}>Add Another Investment</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('FinanceChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  investmentCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  investmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  institutionName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  investmentType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editInvestmentButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editInvestmentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteInvestmentButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteInvestmentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherInvestmentButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherInvestmentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});